import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { Repeat, TrendingUp, TrendingDown, Minus } from "lucide-react";

const RANGES = [30, 90, 365];
const TREND = { up: { icon: TrendingUp, color: "#EF4444" }, down: { icon: TrendingDown, color: "#22C55E" }, flat: { icon: Minus, color: "#9CA3AF" } };

export default function RepeatLocations() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const load = useCallback(() => client.get("/repeat-locations", { params: { days } }).then((r) => setData(r.data)).catch(() => {}), [days]);
  useEffect(() => { load(); }, [load]);
  const withCoords = (data?.locations || []).filter((l) => l.lat != null && l.lng != null);
  const max = Math.max(1, ...(data?.locations || []).map((l) => l.count));

  return (
    <Layout>
      <PageHeader title="Repeat Location Analysis" sub="Highest call-volume locations · heat map · trends · pattern analysis only (not a risk designation)"
        right={<div className="flex gap-1">{RANGES.map((r) => (
          <button key={r} data-testid={`repeat-range-${r}`} onClick={() => setDays(r)}
            className={`px-2.5 py-1.5 text-[10px] font-head font-bold rounded-sm border ${days === r ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "border-[#2C2D35] text-[#9CA3AF]"}`}>{r}D</button>))}</div>} icon={Repeat} />
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row" data-testid="repeat-locations-page">
        <div className="lg:w-1/2 h-64 lg:h-auto border-b lg:border-b-0 lg:border-r border-[#2C2D35]">
          {withCoords.length > 0 ? (
            <MapContainer center={[withCoords[0].lat, withCoords[0].lng]} zoom={12} style={{ height: "100%", width: "100%", background: "#0d0e11" }}>
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" attribution="&copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors" />
              {withCoords.map((l) => (
                <CircleMarker key={l.address} center={[l.lat, l.lng]} radius={6 + 18 * (l.count / max)}
                  pathOptions={{ color: "#EF4444", fillColor: "#EF4444", fillOpacity: 0.35, weight: 1 }}>
                  <Popup><div className="font-mono text-xs"><b>{l.address}</b><br />{l.count} calls / {data.days}d<br />{l.top_types.map(([t, n]) => `${t} (${n})`).join(", ")}</div></Popup>
                </CircleMarker>))}
            </MapContainer>
          ) : <div className="h-full flex items-center justify-center font-mono text-[11px] text-[#6B7280]">No geocoded repeat locations in range.</div>}
        </div>
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-left">
            <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase sticky top-0 bg-[#0d0e11]">
              <tr><th className="px-3 py-2">#</th><th className="px-2">Location</th><th className="px-2">Calls</th><th className="px-2">Trend</th><th className="px-2">Top Types</th><th className="px-2">Last</th></tr>
            </thead>
            <tbody>
              {(data?.locations || []).map((l, i) => {
                const T = TREND[l.trend];
                return (
                  <tr key={l.address} className="border-b border-[#1E1F24]" data-testid={`repeat-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#6B7280]">{i + 1}</td>
                    <td className="px-2 text-xs text-white">{l.address}
                      <div className="h-1 mt-1 rounded bg-[#1E1F24]"><div className="h-1 rounded bg-[#EF4444]" style={{ width: `${100 * l.count / max}%` }} /></div></td>
                    <td className="px-2 font-mono font-bold text-sm">{l.count}</td>
                    <td className="px-2"><T.icon size={14} style={{ color: T.color }} /></td>
                    <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{l.top_types.map(([t, n]) => `${t} (${n})`).join(" · ")}</td>
                    <td className="px-2 font-mono text-[10px] text-[#6B7280]">{l.last_ts ? new Date(l.last_ts).toLocaleDateString("en-GB") : "—"}</td>
                  </tr>);
              })}
              {data && !data.locations.length && <tr><td colSpan={6} className="p-4 font-mono text-[11px] text-[#6B7280]">No repeat locations in the selected range.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
